import React, { createContext, ReactNode, useEffect } from "react";
import {
  DEFAULT_THEME_CONFIG,
  THEME_CSS_FILES,
  THEME_DEFINITIONS,
  THEME_STORAGE_KEY,
  ThemeKey
} from "@/modern/constants/themeConstants";
import { useLocalStorage } from "@/modern/hooks/useLocalStorage";

interface ThemeConfig {
  theme: ThemeKey;
}

interface ThemeContextValue {
  theme: ThemeKey;
  setTheme: (theme: ThemeKey) => void;
}

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const loadThemeConfigFromStorage = (): ThemeConfig => {
  if (typeof window === "undefined") return DEFAULT_THEME_CONFIG;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (!stored) return DEFAULT_THEME_CONFIG;
    const parsed = JSON.parse(stored);
    // Validate that the theme exists in THEME_DEFINITIONS
    if (!parsed.theme || !THEME_DEFINITIONS[parsed.theme as ThemeKey]) {
      console.warn(`Invalid theme "${parsed.theme}" in localStorage. Resetting to default.`);
      // Update localStorage with default theme
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(DEFAULT_THEME_CONFIG));
      return DEFAULT_THEME_CONFIG;
    }
    return parsed;
  } catch (error) {
    console.error("Error loading theme config:", error);
    // Clear corrupted data and use default
    try {
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(DEFAULT_THEME_CONFIG));
    } catch (e) {
      console.error("Failed to update localStorage:", e);
    }
    return DEFAULT_THEME_CONFIG;
  }
};

const initializeTheme = (): void => {
  if (typeof document === "undefined") return;
  try {
    const config = loadThemeConfigFromStorage();
    const themeDefinition = THEME_DEFINITIONS[config.theme];
    // Additional safety check
    if (!themeDefinition) {
      console.error(`Theme definition not found for "${config.theme}". Using default.`);
      const defaultDefinition = THEME_DEFINITIONS[DEFAULT_THEME_CONFIG.theme];
      const colorScheme = defaultDefinition?.colorScheme || "light";
      if (colorScheme === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
      return;
    }
    // Apply data-theme attribute for Tailwind and existing CSS
    document.documentElement.dataset.theme = themeDefinition.colorScheme;
  } catch (error) {
    console.error("Error initializing theme:", error);
    // Fallback to light mode on any error
    document.documentElement.classList.remove("dark");
  }
};

// Initialize theme before React renders to prevent flicker
initializeTheme();

const shadowRoots = new Set<ShadowRoot>();

// Intercept Shadow DOM creation to sync themes automatically
const originalAttachShadow = Element.prototype.attachShadow;
Element.prototype.attachShadow = function(init: ShadowRootInit) {
  const shadowRoot = originalAttachShadow.call(this, init);
  shadowRoots.add(shadowRoot);

  // Apply current color scheme to new Shadow DOM
  const currentTheme = document.documentElement.dataset.theme;
  if (currentTheme) {
    (this as HTMLElement).dataset.theme = currentTheme;
  }

  return shadowRoot;
};

const syncThemeToShadowRoots = (colorScheme: "light" | "dark"): void => {
  shadowRoots.forEach((shadowRoot) => {
    try {
      const host = shadowRoot.host as HTMLElement;
      if (host) {
        host.dataset.theme = colorScheme;
      }
    } catch (error) {
      console.warn("Failed to sync theme to shadow root:", error);
    }
  });
};

const loadThemeCSS = (themeName: ThemeKey): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cssPath = THEME_CSS_FILES[themeName];
    const existingLink = document.getElementById("theme-stylesheet") as HTMLLinkElement;

    // Skip if already loaded
    if (existingLink?.href.includes(cssPath)) {
      resolve();
      return;
    }

    // Create new stylesheet link
    const newLink = document.createElement("link");
    newLink.rel = "stylesheet";
    newLink.href = cssPath;
    newLink.onload = () => {
      newLink.id = "theme-stylesheet";
      // Remove old stylesheet after new one is loaded (prevents flicker)
      if (existingLink && existingLink !== newLink) {
        existingLink.remove();
      }
      resolve();
    };
    newLink.onerror = () => reject(new Error(`Failed to load theme: ${themeName}`));
    // Insert new stylesheet (will load in parallel with old one)
    if (existingLink) {
      existingLink.after(newLink);
    } else {
      document.head.appendChild(newLink);
    }
  });
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // State with cross-tab synchronization
  const [config, setConfig] = useLocalStorage<ThemeConfig>(
    THEME_STORAGE_KEY,
    DEFAULT_THEME_CONFIG
  );
  const { theme } = config;

  const setTheme = (newTheme: ThemeKey) => {
    setConfig({ theme: newTheme });
  };

  // Update dark class based on theme's color scheme
  useEffect(() => {
    const themeDefinition = THEME_DEFINITIONS[theme];
    // Safety check: if theme definition doesn't exist, reset to default
    if (!themeDefinition) {
      console.error(`Theme definition not found for "${theme}". Resetting to default.`);
      setTheme(DEFAULT_THEME_CONFIG.theme);
      return;
    }
    const colorScheme = themeDefinition.colorScheme;
    document.documentElement.dataset.theme = colorScheme;
    syncThemeToShadowRoots(colorScheme);
  }, [theme, setTheme]);

  // Load theme CSS file when theme changes
  useEffect(() => {
    const existingLink = document.getElementById("theme-stylesheet") as HTMLLinkElement;
    const cssPath = THEME_CSS_FILES[theme];
    // Skip if already loaded
    if (existingLink?.href.includes(cssPath)) return;

    loadThemeCSS(theme).catch((error) => {
      console.error("Failed to load theme CSS:", error);
      // Fallback to light theme
      if (theme !== "light") {
        loadThemeCSS("light");
      }
    });
  }, [theme]);

  const value: ThemeContextValue = {
    theme,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export { ThemeContext };
export type { ThemeContextValue };
